import React, { useState, useRef, useEffect } from "react";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { getProgressColor, ANIMATION_DURATION } from "./utils";

// Global ref to track if progress has animated (persists across all renders)
const progressHasAnimatedRef = { current: false };

export { progressHasAnimatedRef };

export default function CircularProgress({
  percentage,
  text,
  shouldAnimate,
  isDark,
}) {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const animationRef = useRef(null);
  const hasAnimatedOnceRef = useRef(false);

  useEffect(() => {
    const endValue = percentage || 0;

    // If we've already animated globally OR should not animate, set directly
    if (
      progressHasAnimatedRef.current ||
      !shouldAnimate ||
      hasAnimatedOnceRef.current
    ) {
      setAnimatedPercentage(endValue);
      return;
    }

    // Cancel any ongoing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easeOut;
      const roundedValue = Math.round(currentValue);

      setAnimatedPercentage(roundedValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
        hasAnimatedOnceRef.current = true;
        progressHasAnimatedRef.current = true;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [percentage, shouldAnimate]);

  return (
    <div className="relative h-[73.6px] w-[92px] shrink-0">
      <div className="absolute inset-0 top-2">
        <CircularProgressbar
          value={animatedPercentage}
          circleRatio={0.5}
          strokeWidth={10.778}
          styles={{
            root: {
              transform: "rotate(0.75turn)",
            },
            path: {
              stroke: getProgressColor(animatedPercentage),
              strokeLinecap: "round",
            },
            trail: {
              stroke: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(18, 18, 18, 0.05)",
              strokeLinecap: "round",
              strokeLinejoin: "round",
            },
          }}
        />
      </div>
      <div className="absolute inset-0 top-20 flex flex-col items-center justify-center">
        <div className="flex flex-col items-center justify-center">
          <span
            className="font-inter font-bold text-center transition-colors duration-300"
            style={{
              fontSize: "18.4px",
              lineHeight: "14.933px",
              letterSpacing: "-0.368px",
              color: getProgressColor(animatedPercentage, text, isDark),
            }}
          >
            {animatedPercentage}%
          </span>
          <span
            className="font-inter font-regular text-center transition-colors duration-300"
            style={{
              fontSize: "12px",
              lineHeight: "1.33em",
              letterSpacing: "-2%",
              color: getProgressColor(animatedPercentage, text, isDark),
            }}
          >
            human
          </span>
        </div>
      </div>
    </div>
  );
}

